/* eslint-disable no-shadow */
/* eslint-disable arrow-body-style */
import { Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BadgeAvatar from '@src/components/BadgeAvatar';
import { useTranslation } from 'react-i18next';
import useRequestAnimationStatus from '@src/hooks/useRequestAnimationStatus';
import { REQUEST_STATUS } from '@src/constants/voice';
import {
  CheckCircleRounded,
  ErrorOutlineRounded,
  MoreVertRounded,
} from '@mui/icons-material';
import { calculateElapsedDateTime } from '@src/utils/date';
import CircularProcess from '@src/components/CircularProcess';
import { MAX_AUDIO_RETENTION_PERIOD } from '@src/constants';
import moment from 'moment';
import actions from '@src/redux/actions';
import apis from '@src/apis';
import { useHistory } from 'react-router-dom';
import routes from '@src/constants/route';
import { StyledTitle } from './index.style';
import ActionDrawer from './ActionDrawer';

const Avatar = ({ voice }) => {
  const { languages } = useSelector((state) => state.language);
  const smallImgSrc =
    voice?.language?.roundImage ||
    languages.find((lang) => lang?.value === voice?.languageCode)?.roundImage;

  return (
    <Box>
      <BadgeAvatar
        width={40}
        img={voice?.roundImage}
        type="image"
        smallImgWidth={10}
        smallImg={smallImgSrc}
      />
    </Box>
  );
};

const Title = ({ request }) => {
  const { t, language } = useTranslation();
  return (
    <StyledTitle>
      <Typography className="title">{request.title}</Typography>
      <Typography className="text-time">
        {calculateElapsedDateTime(request?.createdAt, t)} •{' '}
        {request.characters?.toLocaleString(language) || 0} {t('characters')} •{' '}
        {request.credits?.toLocaleString(language) || 0} {t('credits')}
      </Typography>
    </StyledTitle>
  );
};

const Status = ({ request }) => {
  const { displayProgress, displayStatus } = useRequestAnimationStatus(request);

  return (
    <Box>
      {displayStatus === REQUEST_STATUS.FAILURE && (
        <ErrorOutlineRounded color="error" />
      )}
      {displayStatus === REQUEST_STATUS.DRAFT && (
        <CheckCircleRounded color="info" />
      )}
      {displayStatus === REQUEST_STATUS.IN_PROGRESS && (
        <CircularProcess
          color="blueV2"
          secondColor="blueV2_16"
          thickness={3}
          value={displayProgress}
          size={28}
          textSize={8}
        />
      )}
    </Box>
  );
};

const ButtonAction = ({
  request,
  handleHideButtonCreate,
  handleShowButtonCreate,
  updateRequest,
  deleteRequest,
  onRowClick,
}) => {
  const [openActionDrawer, setOpenActionDrawer] = useState(false);

  const handleOpenActionDrawer = () => {
    handleHideButtonCreate();
    setOpenActionDrawer(true);
  };
  const handleCloseActionDrawer = () => {
    setOpenActionDrawer(false);
    setTimeout(() => {
      handleShowButtonCreate();
    }, 100);
  };

  return (
    <Box>
      <Box role="button" tabIndex="0" onClick={handleOpenActionDrawer}>
        <MoreVertRounded />
      </Box>
      <ActionDrawer
        open={openActionDrawer}
        onOpen={handleOpenActionDrawer}
        onClose={handleCloseActionDrawer}
        request={request}
        updateRequest={updateRequest}
        deleteRequest={deleteRequest}
        onRowClick={onRowClick}
      />
    </Box>
  );
};

const Request = ({
  request,
  handleHideButtonCreate,
  handleShowButtonCreate,
  updateRequest,
  deleteRequest,
  setOpenWarning,
  setDescriptionWarning,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    audioLink,
    retentionPeriod = MAX_AUDIO_RETENTION_PERIOD,
    createdAt,
  } = request || {};

  const audioExpired = moment().isAfter(
    moment(createdAt).add(retentionPeriod, 'days'),
  );

  const { displayStatus } = useRequestAnimationStatus(request);

  const isDisabledAudio =
    !audioLink || audioExpired || displayStatus !== REQUEST_STATUS.SUCCESS;

  const handlePushToMobileTTS = () =>
    history.push(`${routes.MOBILE_TTS}?inSameSession=true`);

  const handleRedirect = (request) => {
    // If request failed
    // Show warning dialog with message request failed
    if (displayStatus === REQUEST_STATUS.FAILURE) {
      setDescriptionWarning(t('warningRequestFailed'));
      setOpenWarning(true);
      return;
    }

    // If request is in progress
    // Navigate to Mobile TTS
    if (displayStatus === REQUEST_STATUS.IN_PROGRESS) {
      handlePushToMobileTTS();
    }

    // If request expired and request multiple voices
    // Show warning dialog with message request expired and multiple voices
    if (isDisabledAudio && request?.sentences?.length > 0) {
      setDescriptionWarning(t('warningRequestExpiredAndMultipleVoice'));
      setOpenWarning(true);
      return;
    }

    // If request expired
    // Show warning dialog with message request expired
    if (isDisabledAudio) {
      setDescriptionWarning(
        t('invalidMobileAudios', {
          expireTime: retentionPeriod,
        }),
      );
      setOpenWarning(true);
      return;
    }

    // If request has sentences
    // Show warning dialog with message request has multiple voices
    if (request?.sentences?.length > 0) {
      setDescriptionWarning(t('warningPushToMobileTTS'));
      setOpenWarning(true);
      return;
    }

    handlePushToMobileTTS();
  };

  const handleRowClick = async (requestId) => {
    const data = await apis.requests.getRequestV2(requestId);

    if (data?.status) {
      const synthesisConfig = data.result;
      const bitrateOfBps = synthesisConfig.bitrate || 0;
      const newSynthesisConfig = {
        ...synthesisConfig,
        bitrate: bitrateOfBps % 1000 === 0 ? bitrateOfBps / 1000 : bitrateOfBps,
      };
      if (synthesisConfig?.sentences?.length > 0) {
        const text = synthesisConfig.sentences
          .map((sentence) => sentence.text)
          .join('\n');
        newSynthesisConfig.text = text;
      }

      const { voice } = synthesisConfig;
      if (voice?.hasEmphasis) {
        newSynthesisConfig.hasEmphasis = voice?.hasEmphasis;
        newSynthesisConfig.emphasis = synthesisConfig?.emphasisIntensity;
      }

      dispatch(
        actions.synthesisRequest.updateSynthesisConfig(newSynthesisConfig),
      );

      dispatch(
        actions.request.addSynthesisResponse({
          requestId: data.result.id,
          ...newSynthesisConfig,
        }),
      );

      dispatch(actions.synthesisRequest.updateLoadingSynthesisConfig(true));
      dispatch(actions.synthesisRequest.updateVoice(synthesisConfig.voice));
      dispatch(actions.audioPlayer.resetAudioPlayer());

      handleRedirect(data.result);
    }
  };

  return (
    <Box key={request.id} className="request-item">
      <Box
        className="request-item-content"
        onClick={() => {
          handleRowClick(request.id);
        }}
      >
        <Avatar voice={request.voice} />
        <Title request={request} />
      </Box>
      <Status request={request} />
      <ButtonAction
        request={request}
        handleHideButtonCreate={handleHideButtonCreate}
        handleShowButtonCreate={handleShowButtonCreate}
        updateRequest={updateRequest}
        deleteRequest={deleteRequest}
        onRowClick={handleRowClick}
      />
    </Box>
  );
};

export default Request;
